import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Card, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Avatar, Chip, IconButton, Button, TextField,
  Alert, Skeleton, InputAdornment, Tooltip,
} from '@mui/material';
import {
  SearchOutlined, GroupOutlined, CheckCircleOutlined,
  RadioButtonUncheckedOutlined, RefreshOutlined,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { api } from '../hooks/useApi';
import { useAuth } from '../contexts/AuthContext';
import { fmtDate } from '../components/DashboardShared';

const PRIMARY = '#1075BA';

function InitialsAvatar({ u }) {
  const initials = `${u.firstName?.[0] || ''}${u.lastName?.[0] || ''}`.toUpperCase();
  const hue = ((u.firstName?.charCodeAt(0) || 65) * 13) % 360;
  return (
    <Avatar sx={{ width: 36, height: 36, fontSize: '0.82rem', fontWeight: 700, bgcolor: `hsl(${hue},50%,88%)`, color: `hsl(${hue},50%,35%)` }}>
      {initials || '?'}
    </Avatar>
  );
}

export default function CollaboratorsPage() {
  const [users, setUsers]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [error, setError]     = useState('');
  const { user: me }          = useAuth();

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      // GET /api/admin-users scoped to current org automatically via authAdmin middleware
      const res = await api.get('/admin-users');
      setUsers(res.data.data || []);
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to load collaborators');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = users.filter(u => {
    const q = search.toLowerCase();
    return !search
      || `${u.firstName} ${u.lastName}`.toLowerCase().includes(q)
      || u.email.toLowerCase().includes(q)
      || u.jobTitle?.toLowerCase().includes(q)
      || u.department?.toLowerCase().includes(q);
  });

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography sx={{ fontSize: '1.4rem', fontWeight: 800, color: 'text.primary' }}>Collaborators</Typography>
          <Typography variant="body2" color="text.secondary">
            {loading ? 'Loading…' : `${users.length} admin user${users.length !== 1 ? 's' : ''} in this organization`}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <TextField
            placeholder="Search by name, email, title…" size="small" value={search}
            onChange={e => setSearch(e.target.value)} sx={{ width: 260 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchOutlined sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment> }}
          />
          <Tooltip title="Refresh">
            <IconButton onClick={load} size="small" sx={{ border: `1px solid ${alpha(PRIMARY, 0.2)}`, borderRadius: 2, width: 36, height: 36 }}>
              <RefreshOutlined sx={{ fontSize: 18, color: PRIMARY }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Card sx={{ borderRadius: 3 }}>
        {loading ? (
          <Box sx={{ p: 2.5 }}>
            {[...Array(4)].map((_, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5 }}>
                <Skeleton variant="circular" width={36} height={36} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton width="38%" />
                  <Skeleton width="26%" height={13} />
                </Box>
                <Skeleton width={90} height={22} sx={{ borderRadius: 1 }} />
                <Skeleton width={60} />
              </Box>
            ))}
          </Box>
        ) : filtered.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <GroupOutlined sx={{ fontSize: 48, color: alpha(PRIMARY, 0.2), mb: 1.5 }} />
            <Typography variant="body2" color="text.secondary">
              {search ? 'No collaborators match your search.' : 'No collaborators in this organization yet.'}
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table sx={{ tableLayout: 'fixed' }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: '30%' }}>Name</TableCell>
                  <TableCell sx={{ width: '22%' }}>Email</TableCell>
                  <TableCell sx={{ width: '14%' }}>Role</TableCell>
                  <TableCell sx={{ width: '14%' }}>Job Title</TableCell>
                  <TableCell sx={{ width: '8%'  }}>Active</TableCell>
                  <TableCell sx={{ width: '12%' }}>Joined</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map(u => (
                  <TableRow key={u._id} hover sx={{ '&:hover': { background: alpha(PRIMARY, 0.025) } }}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <InitialsAvatar u={u} />
                        <Box sx={{ minWidth: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {u.firstName} {u.lastName}
                            </Typography>
                            {u._id === me?._id && (
                              <Chip label="You" size="small" sx={{ height: 16, fontSize: '0.62rem', fontWeight: 700, background: alpha(PRIMARY, 0.1), color: PRIMARY, '& .MuiChip-label': { px: 0.75 } }} />
                            )}
                          </Box>
                          {u.department && (
                            <Typography variant="caption" color="text.secondary">{u.department}</Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                        {u.email}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      {u.role ? (
                        <Chip
                          label={u.role.name}
                          size="small"
                          sx={{
                            fontSize: '0.7rem', height: 20, fontWeight: 600,
                            background: alpha(u.role.color || PRIMARY, 0.1),
                            color: u.role.color || PRIMARY,
                            border: `1px solid ${alpha(u.role.color || PRIMARY, 0.2)}`,
                          }}
                        />
                      ) : '—'}
                    </TableCell>

                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {u.jobTitle || '—'}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      {u.isActive
                        ? <CheckCircleOutlined sx={{ fontSize: 18, color: '#00a882' }} />
                        : <RadioButtonUncheckedOutlined sx={{ fontSize: 18, color: alpha('#000', 0.2) }} />
                      }
                    </TableCell>

                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {fmtDate(u.createdAt)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>
    </Box>
  );
}